import { Component, forwardRef, Input } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { ChipItem } from '@shared/components/chip-input/chip-input.component';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

@Component({
  selector: 'app-report-recipients-input',
  template: `
    <tag-input
      [ngModel]="chips"
      (ngModelChange)="onChipsChange($event)"
      [maxItems]="maxItems"
      [placeholder]="placeholder"
      theme="bootstrap"
    ></tag-input>
    <small class="text-danger d-block mt-1" *ngIf="errorMessage">{{ errorMessage }}</small>
  `,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ReportRecipientsInputComponent),
      multi: true
    }
  ],
  standalone: false
})
export class ReportRecipientsInputComponent implements ControlValueAccessor {
  @Input() public allowedDomains: string[] = [];
  @Input() public domainRestricted = false;
  @Input() public placeholder = '';

  public readonly maxItems = 5;
  public chips: ChipItem[] = [];
  public errorMessage = '';

  private onChange: (value: string[]) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  public writeValue(value: string[]): void {
    this.chips = (value || []).map((email: string) => ({ display: email, value: email }));
  }

  public registerOnChange(fn: (value: string[]) => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  public onChipsChange(chips: ChipItem[]): void {
    const valid = (chips || []).filter((chip: ChipItem) => this.validateEmail(chip.value));

    this.chips = valid;
    this.onChange(valid.map((chip: ChipItem) => chip.value));
    this.onTouched();
  }

  public validateEmail(email: string): boolean {
    const normalized = (email || '').trim().toLowerCase();

    if (!EMAIL_PATTERN.test(normalized)) {
      this.errorMessage = `${email} is not a valid email address`;
      return false;
    }

    if (this.domainRestricted) {
      const domain = normalized.split('@').pop();
      const allowed = (this.allowedDomains || []).map((d: string) => d.toLowerCase());

      if (allowed.length > 0 && !allowed.includes(domain)) {
        this.errorMessage = `${domain} is not a domain used by a recruiter in your organization`;
        return false;
      }
    }

    this.errorMessage = '';
    return true;
  }
}
